import React, {
	MutableRefObject,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Layer,
	MapRef,
	Source,
} from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { useQuery } from 'react-query';
import debounce from 'lodash.debounce';
import type { GetPortsInBoundsRequest } from '@api/features/geo/getPortsInBounds';
import { getPortsInBounds } from '@client/lib/api';
import type { Viewport } from '../Map';

export const PORTS_MIN_ZOOM = 6;

type PortsGeoJSONData = {
	type: 'FeatureCollection';
	features: {
		type: 'Feature';
		geometry: {
			type: 'Point';
			coordinates: [number, number];
		};
		properties: {
			id: number;
			name: string;
		};
	}[];
};

const wrapLongitude = (longitude: number) => ((((longitude + 180) % 360) + 360) % 360) - 180;

const getRoundedBounds = (map: mapboxgl.Map): GetPortsInBoundsRequest => {
	const bounds = map.getBounds();
	const west = Math.floor(bounds.getWest());
	const east = Math.ceil(bounds.getEast());
	const south = Math.max(-90, Math.floor(bounds.getSouth()));
	const north = Math.min(90, Math.ceil(bounds.getNorth()));

	if (east - west >= 360) {
		return { west: -180, south, east: 180, north };
	}

	return {
		west: wrapLongitude(west),
		south,
		east: wrapLongitude(east),
		north,
	};
};

type PortsLayerProps = {
	viewport: Viewport;
	mapRef: MutableRefObject<MapRef | undefined>;
};

const PortsLayer = ({ viewport, mapRef }: PortsLayerProps) => {
	const [bounds, setBounds] = useState<GetPortsInBoundsRequest | null>(null);

	const zoom = viewport.zoom ?? 0;
	const isZoomedIn = zoom >= PORTS_MIN_ZOOM;

	const updateBounds = useMemo(() => debounce(() => {
		const map = mapRef.current?.getMap();

		if (map != null) {
			setBounds(getRoundedBounds(map));
		}
	}, 300), [mapRef]);

	useEffect(() => () => updateBounds.cancel(), [updateBounds]);

	useEffect(() => {
		if (isZoomedIn) {
			updateBounds();
		}
	}, [isZoomedIn, viewport.latitude, viewport.longitude, zoom, updateBounds]);

	const { data: ports } = useQuery(
		['ports-in-bounds', bounds],
		() => (bounds == null ? [] : getPortsInBounds(bounds)),
		{
			enabled: isZoomedIn && bounds != null,
			keepPreviousData: true,
			staleTime: Infinity,
		},
	);

	const data = useMemo<PortsGeoJSONData>(() => ({
		type: 'FeatureCollection',
		features: (ports ?? []).map((port) => ({
			type: 'Feature',
			geometry: {
				type: 'Point',
				coordinates: [port.longitude, port.latitude],
			},
			properties: {
				id: port.id,
				name: port.name,
			},
		})),
	}), [ports]);

	return (
		<Source
			id="ports-source"
			type="geojson"
			data={data}
		>
			<Layer
				id="ports-layer"
				type="symbol"
				minzoom={PORTS_MIN_ZOOM}
				layout={{
					'icon-image': 'anchor',
					'text-field': ['step', ['zoom'], '', 8, ['get', 'name']],
					'text-size': 11,
					'text-offset': [0, 1.1],
					'text-anchor': 'top',
					'text-optional': true,
				}}
				paint={{
					'text-color': '#ffffff',
					'text-halo-color': '#1d3557',
					'text-halo-width': 1.5,
				}}
			/>
		</Source>
	);
};

export default PortsLayer;
